/**
 * One conversation in the Inbox (tracker item 56): its messages oldest
 * first, the reply box and internal notes, and the staff actions (status,
 * assignee, linked job, customer). For UNMATCHED mail: attach to a customer,
 * merge into a conversation, or discard. Opening it marks it read.
 *
 * Message text is always rendered as plain text (never as HTML): the inbound
 * text without quoted history (displayText), with "Show full message" for the
 * text as received.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IConversationsApi, IJobsApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { CustomerPicker } from '../../components/CustomerPicker';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { showToast } from '../../components/Toast';
import {
  CONVERSATION_STATUS_LABELS,
  conversationErrorMessage,
  conversationStatusClass,
  deliveryText,
  formatWhen,
  inboxAllowed,
  messageAuthor,
  sendKey,
} from './inboxUi';
import { notifyInboxUnreadChanged } from './useInboxNav';

type Api = IConversationsApi & IJobsApi & IUsersApi;

interface Props {
  conversationId: string;
  inboxAccess: types.InboxAccess;
  /** The conversation changed (status, assignee, a new message): the list reloads */
  onChanged: () => void;
  /** UNMATCHED mail was discarded or merged away: the page leaves it */
  onRemoved?: (mergedInto?: string) => void;
}

const toneClass = { ok: 'text-green-700', pending: 'text-gray-600', bad: 'text-red-700' } as const;

export function MessageItem({ message, customerName }: { message: types.ConversationMessage; customerName: string | null }) {
  const [full, setFull] = useState(false);
  const delivery = deliveryText(message.delivery);
  const hasMore = message.bodyText.trim() !== message.displayText.trim();
  const tone =
    message.direction === 'NOTE' ? 'bg-yellow-50 ring-yellow-200' : message.direction === 'OUTBOUND' ? 'bg-blue-50 ring-blue-100' : 'bg-white ring-gray-200';
  return (
    <li className={`rounded-lg p-3 ring-1 ${tone}`} data-testid="conversation-message" data-direction={message.direction}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-gray-600">
        <span className="font-semibold text-gray-800">{messageAuthor(message, customerName)}</span>
        <time dateTime={message.createdAt}>{formatWhen(message.createdAt)}</time>
      </div>
      {(message.isAutoReply || message.isUnsubscribeRequest) && (
        <div className="mt-1 flex flex-wrap gap-1 text-xs">
          {message.isAutoReply && <span className="rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">Automatic reply</span>}
          {message.isUnsubscribeRequest && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800">Asks to stop emails (nothing was changed automatically)</span>
          )}
        </div>
      )}
      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-900" data-testid="message-text">{full ? message.bodyText : message.displayText}</p>
      {hasMore && (
        <button type="button" className="mt-1 text-xs font-semibold text-blue-700 hover:underline" onClick={() => setFull((v) => !v)} aria-expanded={full}>
          {full ? 'Show less' : 'Show full message'}
        </button>
      )}
      {message.attachments && (message.attachments.saved.length > 0 || message.attachments.dropped.length > 0) && (
        <ul className="mt-2 space-y-0.5 text-xs" aria-label="Attachments">
          {message.attachments.saved.map((a) => <li key={a.attachmentId} className="text-gray-700">Attachment saved on the customer: {a.filename}</li>)}
          {message.attachments.dropped.map((a, i) => <li key={`${a.filename}-${i}`} className="text-red-700">{a.filename}: {a.reason}</li>)}
        </ul>
      )}
      {delivery && <p className={`mt-1 text-xs font-medium ${toneClass[delivery.tone]}`} data-testid="message-delivery">{delivery.label}</p>}
    </li>
  );
}

export function ConversationThread({ conversationId, inboxAccess, onChanged, onRemoved }: Props) {
  const api = useMemo<Api>(() => createAPIClient(), []);
  const [detail, setDetail] = useState<types.ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);
  // The parent passes a new callback each render; keep the latest without reloading
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async (markRead = false) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getConversation(conversationId);
      if (id !== requestId.current) return;
      setDetail(result);
      if (markRead && result.conversation.unread) {
        try {
          await api.markConversationRead(conversationId);
          if (id === requestId.current) setDetail((d) => (d ? { ...d, conversation: { ...d.conversation, unread: false } } : d));
          notifyInboxUnreadChanged();
          onChangedRef.current();
        } catch {
          // Reading still works; the badge catches up later
        }
      }
    } catch (cause) {
      if (id === requestId.current) setError(conversationErrorMessage(cause, 'Could not load the conversation'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [api, conversationId]);

  useEffect(() => {
    setDetail(null);
    setActionError(null);
    void load(true);
  }, [load]);

  const act = async (work: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      if (success) showToast('success', success);
      await load();
      onChangedRef.current();
      return true;
    } catch (cause) {
      setActionError(conversationErrorMessage(cause, 'That did not work'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loading && !detail) return <p role="status" className="p-4 text-gray-600">Loading conversation…</p>;
  if (error) return <p role="alert" className="m-4 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>;
  if (!detail) return null;

  const c = detail.conversation;
  const unmatched = c.status === 'UNMATCHED';

  return (
    <section aria-label="Conversation" className="flex flex-col gap-4">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold text-gray-900 break-words">{c.subject}</h2>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${conversationStatusClass(c.status)}`} data-testid="conversation-status">
            {CONVERSATION_STATUS_LABELS[c.status]}
          </span>
          {c.origin === 'PORTAL' && <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">Customer portal</span>}
        </div>
        <p className="text-sm text-gray-700">
          {c.customer ? <Link className="text-blue-700 hover:underline" to={`/customers/${c.customer.id}`}>{c.customer.name}</Link> : 'Not linked to a customer'}
          {c.customer?.email && <span className="text-gray-500"> · {c.customer.email}</span>}
          {c.jobId && <> · <Link className="text-blue-700 hover:underline" to={`/jobs/${c.jobId}`}>Linked job</Link></>}
          {c.invoiceId && <> · <Link className="text-blue-700 hover:underline" to={`/invoices/${c.invoiceId}`}>Linked invoice</Link></>}
          {c.estimateId && <> · <Link className="text-blue-700 hover:underline" to={`/estimates/${c.estimateId}`}>Linked estimate</Link></>}
        </p>
        {c.customerOptedOut && (
          <p role="note" className="rounded bg-amber-50 p-2 text-sm text-amber-900">
            This customer opted out of emails. Replies go out only because they wrote in this conversation; you cannot start a new email conversation with them.
          </p>
        )}
        {unmatched && c.heldForConversationId && (
          <p role="note" className="rounded bg-amber-50 p-2 text-sm text-amber-900">
            Held: this reply was addressed to <Link className="font-semibold underline" to={`/inbox/${c.heldForConversationId}`}>another conversation</Link> but came from an address that is not on that customer.
          </p>
        )}
      </header>

      {actionError && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{actionError}</p>}

      {unmatched ? (
        <UnmatchedActions conversation={c} busy={busy} act={act} api={api} onRemoved={onRemoved} />
      ) : (
        <ConversationActions conversation={c} busy={busy} act={act} api={api} inboxAccess={inboxAccess} />
      )}

      <ol className="space-y-3" aria-label="Messages">
        {detail.messages.map((m) => <MessageItem key={m.id} message={m} customerName={c.customer?.name ?? null} />)}
      </ol>

      {!unmatched && <Composer conversation={c} api={api} onSent={async () => { await load(); onChangedRef.current(); notifyInboxUnreadChanged(); }} />}
    </section>
  );
}

type Act = (work: () => Promise<unknown>, success?: string) => Promise<boolean>;

function ConversationActions({ conversation: c, busy, act, api, inboxAccess }: { conversation: types.Conversation; busy: boolean; act: Act; api: Api; inboxAccess: types.InboxAccess }) {
  const [users, setUsers] = useState<types.AssignableUser[]>([]);
  const [jobs, setJobs] = useState<types.Job[]>([]);
  const [changingCustomer, setChangingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const list = await api.listAssignableUsers();
        if (active) setUsers(list.filter((u) => inboxAllowed(u.role, inboxAccess)));
      } catch {
        if (active) setUsers([]);
      }
    })();
    return () => { active = false; };
  }, [api, inboxAccess]);

  const customerId = c.customer?.id;
  useEffect(() => {
    if (!customerId) return undefined;
    let active = true;
    void (async () => {
      try {
        const result = await api.listJobs(1, 100, { customerId });
        if (active) setJobs(result.data);
      } catch {
        if (active) setJobs([]);
      }
    })();
    return () => { active = false; };
  }, [api, customerId]);

  const update = (data: types.UpdateConversationInput, message: string) => act(() => api.updateConversation(c.id, data), message);
  const assigneeKnown = !c.assignedUserId || users.some((u) => u.id === c.assignedUserId);

  return (
    <div className="space-y-3 rounded-lg bg-gray-50 p-3" aria-label="Conversation actions" role="group">
      <div className="flex flex-wrap gap-2">
        {c.status !== 'CLOSED' ? (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => void update({ status: 'CLOSED' }, 'Conversation closed')}>Close</button>
        ) : (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => void update({ status: 'OPEN' }, 'Conversation reopened')}>Reopen</button>
        )}
        {c.status === 'OPEN' && (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => void update({ status: 'WAITING_ON_CUSTOMER' }, 'Marked as waiting on the customer')}>
            Waiting on customer
          </button>
        )}
        {c.status === 'WAITING_ON_CUSTOMER' && (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => void update({ status: 'OPEN' }, 'Marked as open')}>Mark open</button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="conversation-assignee" className={labelClass}>Assigned to</label>
          <select
            id="conversation-assignee"
            className={inputClass}
            value={c.assignedUserId ?? ''}
            disabled={busy}
            onChange={(e) => void update({ assignedUserId: e.target.value || null }, e.target.value ? 'Conversation assigned' : 'Conversation unassigned')}
          >
            <option value="">Unassigned</option>
            {!assigneeKnown && c.assignee && <option value={c.assignee.id}>{c.assignee.name}</option>}
            {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="conversation-job" className={labelClass}>Linked job</label>
          <select
            id="conversation-job"
            className={inputClass}
            value={c.jobId ?? ''}
            disabled={busy || !customerId}
            onChange={(e) => void update({ jobId: e.target.value || null }, e.target.value ? 'Job linked' : 'Job unlinked')}
          >
            <option value="">No job</option>
            {c.jobId && !jobs.some((j) => j.id === c.jobId) && <option value={c.jobId}>Current job</option>}
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{[j.scheduledDate ?? 'Unscheduled', j.property?.address, j.status].filter(Boolean).join(' · ')}</option>
            ))}
          </select>
        </div>
      </div>
      {c.origin === 'EMAIL' && (
        changingCustomer ? (
          <div className="space-y-2">
            <CustomerPicker id="conversation-customer" label="Move to customer" value={newCustomer} onChange={(id) => setNewCustomer(id)} />
            <p className="text-xs text-gray-600">The job, invoice and estimate links are dropped (they belong to the old customer).</p>
            <div className="flex gap-2">
              <button
                type="button"
                className={primaryButtonClass}
                disabled={busy || !newCustomer}
                onClick={() => void (async () => { if (await update({ customerId: newCustomer }, 'Customer changed')) setChangingCustomer(false); })()}
              >
                Change customer
              </button>
              <button type="button" className={secondaryButtonClass} onClick={() => setChangingCustomer(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-sm font-semibold text-blue-700 hover:underline" onClick={() => setChangingCustomer(true)}>Link to another customer</button>
        )
      )}
    </div>
  );
}

function UnmatchedActions({ conversation: c, busy, act, api, onRemoved }: { conversation: types.Conversation; busy: boolean; act: Act; api: Api; onRemoved?: (mergedInto?: string) => void }) {
  const [customerId, setCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<types.Conversation[]>([]);
  const [target, setTarget] = useState(c.heldForConversationId ?? '');

  const find = async () => {
    try {
      const result = await api.listConversations({ search: search.trim(), limit: 20 });
      setCandidates(result.data);
    } catch {
      setCandidates([]);
    }
  };

  return (
    <div className="space-y-4 rounded-lg bg-gray-50 p-3" role="group" aria-label="Unmatched email actions">
      <div className="space-y-2">
        <CustomerPicker id="unmatched-customer" label="Attach to a customer" value={customerId} onChange={(id) => setCustomerId(id)} />
        <button
          type="button"
          className={primaryButtonClass}
          disabled={busy || !customerId}
          onClick={() => void act(() => api.assignConversationCustomer(c.id, { customerId }), 'Email attached to the customer')}
        >
          Attach to customer
        </button>
      </div>
      <div className="space-y-2">
        <label htmlFor="merge-search" className={labelClass}>Or merge into a conversation</label>
        <div className="flex gap-2">
          <input id="merge-search" className={inputClass} value={search} placeholder="Subject or customer" onChange={(e) => setSearch(e.target.value)} />
          <button type="button" className={secondaryButtonClass} onClick={() => void find()}>Find</button>
        </div>
        {(candidates.length > 0 || c.heldForConversationId) && (
          <select id="merge-target" aria-label="Conversation to merge into" className={inputClass} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose a conversation</option>
            {c.heldForConversationId && !candidates.some((x) => x.id === c.heldForConversationId) && (
              <option value={c.heldForConversationId}>The conversation it was addressed to</option>
            )}
            {candidates.map((x) => <option key={x.id} value={x.id}>{x.subject} · {x.customer?.name ?? ''}</option>)}
          </select>
        )}
        <button
          type="button"
          className={secondaryButtonClass}
          disabled={busy || !target}
          onClick={() => void (async () => {
            const done = await act(() => api.assignConversationCustomer(c.id, { conversationId: target }), 'Email merged into the conversation');
            if (done) onRemoved?.(target);
          })()}
        >
          Merge
        </button>
      </div>
      <button
        type="button"
        className="text-sm font-semibold text-red-700 hover:underline"
        disabled={busy}
        onClick={() => void (async () => {
          if (!window.confirm('Discard this email? It is kept but leaves the Unmatched list.')) return;
          const done = await act(() => api.discardConversation(c.id), 'Email discarded');
          if (done) onRemoved?.();
        })()}
      >
        Discard
      </button>
    </div>
  );
}

function Composer({ conversation: c, api, onSent }: { conversation: types.Conversation; api: Api; onSent: () => Promise<void> }) {
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const key = useRef(sendKey('reply'));

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim()) return setError(mode === 'reply' ? 'Write a reply.' : 'Write a note.');
    setSending(true);
    setError(null);
    try {
      if (mode === 'reply') {
        const result = await api.replyToConversation(c.id, text, key.current);
        const delivery = deliveryText(result.message.delivery);
        if (delivery?.tone === 'bad') showToast('warning', `Reply saved, but ${delivery.label.toLowerCase()}`);
        else showToast('success', c.origin === 'PORTAL' ? 'Reply posted in the customer portal' : 'Reply queued');
      } else {
        await api.addConversationNote(c.id, text);
        showToast('success', 'Note added');
      }
      setText('');
      key.current = sendKey('reply');
      await onSent();
    } catch (cause) {
      setError(conversationErrorMessage(cause, mode === 'reply' ? 'Could not send the reply' : 'Could not add the note'));
      key.current = sendKey('reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={(e) => void send(e)} className="space-y-2 rounded-lg bg-white p-3 ring-1 ring-gray-200" aria-label="Write">
      <div role="tablist" className="flex gap-2">
        {(['reply', 'note'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            className={`rounded px-3 py-1 text-sm font-semibold ${mode === m ? 'bg-blue-100 text-blue-800' : 'text-gray-600 hover:bg-gray-100'}`}
            onClick={() => { setMode(m); setError(null); }}
          >
            {m === 'reply' ? 'Reply' : 'Internal note'}
          </button>
        ))}
      </div>
      <label htmlFor="composer-text" className="sr-only">{mode === 'reply' ? 'Reply' : 'Internal note'}</label>
      <textarea
        id="composer-text"
        className={inputClass}
        rows={4}
        maxLength={10000}
        value={text}
        placeholder={mode === 'reply' ? (c.origin === 'PORTAL' ? 'Reply in the customer portal…' : `Reply by email to ${c.customer?.name ?? 'the customer'}…`) : 'Only staff see notes'}
        onChange={(e) => setText(e.target.value)}
      />
      {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <button type="submit" className={primaryButtonClass} disabled={sending}>
        {sending ? 'Sending…' : mode === 'reply' ? 'Send reply' : 'Add note'}
      </button>
    </form>
  );
}
